import React, { useState, useEffect } from 'react';
import appScriptService from '../../utils/appScriptService';
import aiSummaryService from '../../utils/aiSummaryService';
import googleTTSService from '../../utils/googleTTSService';
import ttsModuleService from '../../utils/ttsModuleService';

const TTSManager = () => {
  // 기본 상태 관리
  const [selectedModule, setSelectedModule] = useState('LM2001020230');
  const [scriptData, setScriptData] = useState(null);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [isGeneratingTTS, setIsGeneratingTTS] = useState(false);
  const [audioUrls, setAudioUrls] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [ttsProgress, setTtsProgress] = useState({});
  const [loadedModuleData, setLoadedModuleData] = useState(null);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(false);

  // 편집 관련 상태
  const [isEditingScript, setIsEditingScript] = useState(false);
  const [editedScript, setEditedScript] = useState(null);

  // 전체 데이터 캐시 (성능 최적화)
  const [cachedSummaryData, setCachedSummaryData] = useState(null);
  const [cachedTTSData, setCachedTTSData] = useState({});
  const [isInitialDataLoading, setIsInitialDataLoading] = useState(false);

  // NCS 모듈 목록 (동적 데이터 확인)
  const [modules, setModules] = useState([
    { id: 'LM2001020230', name: '프로그래밍 언어 응용', hasData: false, isChecking: true },
    { id: 'LM2001020231', name: '프로그래밍 언어 활용', hasData: false, isChecking: true },
    { id: 'LM2001020224', name: '화면설계', hasData: false, isChecking: true },
    { id: 'LM2001020225', name: '화면구현', hasData: false, isChecking: true },
    { id: 'LM2001020211', name: '서버프로그램구현', hasData: false, isChecking: true },
    { id: 'LM2001020227', name: '애플리케이션테스트수행' ,hasData: false, isChecking: true },
    { id: 'LM2001020205', name: '데이터입출력구현'       ,hasData: false, isChecking: true },
    { id: 'LM2001020206', name: '통합구현'               ,hasData: false, isChecking: true },
    { id: 'LM2001020214', name: '애플리케이션배포'       ,hasData: false, isChecking: true },
    { id: 'LM2001020212', name: '인터페이스구현'         ,hasData: false, isChecking: true },
    // 추가 카테고리,hasData: false, isChecking: true 
    { id: 'BE001_SPRING', name: 'Spring 백엔드'          ,hasData: false, isChecking: true },
    { id: 'FE001_REACT', name: 'React 프론트엔드'        ,hasData: false, isChecking: true },
    { id: 'HR001_COMMON', name: '공통 면접질문'          ,hasData: false, isChecking: true },
  ]);

  // 컴포넌트 마운트 시 모듈별 데이터 존재 여부 확인 및 TTS 진행률 콜백 설정
  useEffect(() => {
    loadAllDataOnce();

    // TTS 진행률 콜백 설정
    ttsModuleService.setProgressCallback((key, progress) => {
      setTtsProgress(prev => ({
        ...prev,
        [key]: progress
      }));
    });
  }, []);

  // 한 번만 전체 데이터 로드하고 메모리에 캐시
  const loadAllDataOnce = async () => {
    if (isInitialDataLoading || cachedSummaryData) {
      console.log('📦 이미 데이터가 로드되었거나 로딩 중입니다.');
      return;
    }

    setIsInitialDataLoading(true);
    console.time('🚀 전체 데이터 로드');

    try {
      // 1. AI요약 데이터 한 번만 로드
      console.log('📊 AI요약 데이터 로드 중...');
      const allSummaryData = await appScriptService.getData('AI요약');
      setCachedSummaryData(allSummaryData || []);

      console.log(`✅ AI요약 데이터 로드 완료: ${allSummaryData?.length || 0}개`);

      // 2. 과목코드별 그룹핑 (메모리에서 빠르게 처리)
      const summaryByModuleId = {};
      if (allSummaryData && allSummaryData.length > 0) {
        allSummaryData.forEach(item => {
          if (item.과목코드) {
            // 과목코드에서 ID 부분 추출 (LM2001020230_프로그래밍언어응용 -> LM2001020230)
            const moduleId = item.과목코드.includes('_')
              ? item.과목코드.split('_')[0]
              : item.과목코드;

            if (!summaryByModuleId[moduleId]) {
              summaryByModuleId[moduleId] = [];
            }
            summaryByModuleId[moduleId].push(item);
          }
        });
      }

      console.log('🗺️ 그룹핑된 모듈 ID:', Object.keys(summaryByModuleId));

      // 3. TTS 데이터는 필요할 때만 로드 (처음 3개만 테스트)
      console.log('🎵 TTS 데이터 샘플 로드 중...');
      const initialTTSData = {};
      const sampleModules = modules.slice(0, 3); // 처음 3개만

      await Promise.all(
        sampleModules.map(async (module) => {
          try {
            const ttsData = await appScriptService.getTTSScripts(module.id);
            initialTTSData[module.id] = ttsData;
          } catch (error) {
            console.warn(`TTS 데이터 로드 실패 (${module.id}):`, error);
            initialTTSData[module.id] = null;
          }
        })
      );

      setCachedTTSData(initialTTSData);

      // 4. 모듈 상태 업데이트 (메모리에서 처리)
      const updatedModules = modules.map(module => {
        const summaryData = summaryByModuleId[module.id] || [];
        const ttsData = initialTTSData[module.id];

        const hasSummaryData = summaryData.length > 0;
        const hasTTSData = ttsData && ttsData.sections && ttsData.sections.length > 0;

        // 통합구현 디버깅
        if (module.id === 'LM2001020206') {
          console.log(`🔍 통합구현 최종 결과:`, {
            moduleId: module.id,
            summaryDataCount: summaryData.length,
            hasSummaryData,
            ttsData: ttsData ? '있음' : '없음',
            hasTTSData
          });
        }

        return {
          ...module,
          hasData: hasSummaryData || hasTTSData,
          hasSummaryData,
          hasTTSData,
          isChecking: false,
          dataCount: hasSummaryData ? summaryData.length : (hasTTSData ? ttsData.sections.length : 0)
        };
      });

      setModules(updatedModules);

      console.timeEnd('🚀 전체 데이터 로드');
      console.log('✨ 캐시 초기화 완료! 이제 더 빠르게 동작합니다.');

    } catch (error) {
      console.error('❌ 전체 데이터 로드 실패:', error);

      // 실패 시 기존 방식 사용
      console.log('🔄 기존 방식으로 폴백...');
      await checkModulesData();
    } finally {
      setIsInitialDataLoading(false);
    }
  };

  // 모듈별 TTS_Scripts 데이터 존재 여부 확인
  const checkModulesData = async () => {

    // 전체 AI 요약 데이터 확인
    try {
      const allSummaryData = await appScriptService.getData('AI요약');
      console.log('📊 전체 AI요약 데이터:', allSummaryData);
      if (allSummaryData && allSummaryData.length > 0) {
        const uniqueCodes = [...new Set(allSummaryData.map(item => item.과목코드))].filter(code => code);
        console.log('📋 사용 중인 과목코드들:', uniqueCodes);
      }
    } catch (error) {
      console.error('전체 데이터 조회 실패:', error);
    }

    const updatedModules = await Promise.all(
      modules.map(async (module) => {
        try {

          
          // TTS_Scripts 시트에서 해당 모듈 데이터 조회
          const ttsData = await appScriptService.getTTSScripts(module.id);
          
          // AI요약 시트에서도 확인 (과목코드 매핑 개선)
          // 1차: 정확한 일치 시도
          let summaryData = await appScriptService.getData('AI요약', {
            과목코드: module.id
          });

          // 2차: 데이터가 없으면 전체 데이터에서 ID로 시작하는 과목코드 찾기
          if (!summaryData || summaryData.length === 0) {
            const allSummaryData = await appScriptService.getData('AI요약');
            if (allSummaryData && allSummaryData.length > 0) {
              summaryData = allSummaryData.filter(item => {
                if (!item.과목코드) return false;
                // 과목코드를 _로 split해서 첨 번째 부분 비교
                const codeId = item.과목코드.split('_')[0];
                return codeId === module.id;
              });
            }
          }

          // 디버깅: 통합구현 모듈 데이터 확인
          if (module.id === 'LM2001020206') {
            console.log(`🔍 통합구현(${module.id}) 데이터 체크:`, {
              summaryData,
              summaryDataLength: summaryData ? summaryData.length : 0,
              ttsData,
              ttsDataSections: ttsData?.sections?.length || 0
            });
          }

          const hasTTSData = ttsData && ttsData.sections && ttsData.sections.length > 0;
          const hasSummaryData = summaryData && summaryData.length > 0;
          

          
          return {
            ...module,
            hasData: hasTTSData || hasSummaryData,
            hasTTSData: hasTTSData,
            hasSummaryData: hasSummaryData,
            isChecking: false,
            dataCount: hasTTSData ? ttsData.sections.length : (hasSummaryData ? summaryData.length : 0)
          };
        } catch (error) {
          console.error(`❌ ${module.id} 데이터 확인 실패:`, error);
          return {
            ...module,
            hasData: false,
            hasTTSData: false,
            hasSummaryData: false,
            isChecking: false,
            dataCount: 0
          };
        }
      })
    );
    
    setModules(updatedModules);

  };

  // 캐시된 데이터에서 특정 모듈 데이터 가져오기 (매우 빠름)
  const getCachedSummaryData = (moduleId) => {
    if (!cachedSummaryData || cachedSummaryData.length === 0) {
      console.warn('📦 캐시된 데이터가 없습니다. 로드를 기다리세요.');
      return [];
    }

    const result = cachedSummaryData.filter(item => {
      if (!item.과목코드) return false;
      const itemModuleId = item.과목코드.includes('_')
        ? item.과목코드.split('_')[0]
        : item.과목코드;
      return itemModuleId === moduleId;
    });

    console.log(`📊 ${moduleId} 데이터: ${result.length}개 (캐시에서)`);
    return result;
  };

  // TTS 데이터 로드 (필요시만)
  const getCachedTTSData = async (moduleId) => {
    // 이미 캐시되었으면 바로 리턴
    if (cachedTTSData[moduleId] !== undefined) {
      return cachedTTSData[moduleId];
    }

    // 없으면 지금 로드하여 캐시에 저장
    try {
      console.log(`🎵 TTS 데이터 로드: ${moduleId}`);
      const ttsData = await appScriptService.getTTSScripts(moduleId);
      setCachedTTSData(prev => ({ ...prev, [moduleId]: ttsData }));
      return ttsData;
    } catch (error) {
      console.warn(`TTS 로드 실패 (${moduleId}):`, error);
      setCachedTTSData(prev => ({ ...prev, [moduleId]: null }));
      return null;
    }
  };

  // 섹션별 AI 스크립트 생성 상태
  const [generationProgress, setGenerationProgress] = useState({});
  const [isGeneratingStepByStep, setIsGeneratingStepByStep] = useState(false);

  // 1단계: 섹션 구조 생성
  const generateSectionsOutline = async (moduleName, summaryContent) => {
    const outlinePrompt = `You are a senior developer and NCS education expert. Create a podcast section outline for "${moduleName}".

**IMPORTANT: Output must be in Korean language only.**

📋 **Input Data:**
Module: ${moduleName}
NCS Summary: ${summaryContent}

Create 6 podcast sections }`;

    const response = await aiSummaryService.generateSummary(outlinePrompt, 'structured');
    return JSON.parse(response);
  };

  // 2단계: 개별 섹션 상세 내용 생성
  const generateSectionDetail = async (section, moduleName, summaryContent, sectionIndex, totalSections) => {
    const sectionPrompt = `You are a senior developer creating detailed content for section ${sectionIndex + 1} of ${totalSections} in a Korean podcast about "${moduleName}".

**IMPORTANT: Output must be in Korean language only.**

📋 **Section Info:**
Title: ${section.section_title}
Description: ${section.description}
Module: ${moduleName}
NCS Summary: ${summaryContent}

📝 **Requirements for this specific section:**
- Length: 3500-4500 characters (rich content using paid API)
- Style: Conversational Korean podcast ("안녕하세요 여러분", "궁금하지 않으세요?")
- Include 4-6 detailed practical examples
- Interview preparation points for this specific topic
- Beginner-friendly explanations with everyday analogies
- Real workplace scenarios and career advice
- NO CODE: Focus on concepts and principles only

🎯 **Content Structure:**
1. ${sectionIndex === 0 ? 'Opening greeting and topic introduction' : 'Section introduction connecting to previous topic'}
2. Core concept explanation with daily-life analogies
3. 4-6 detailed practical workplace examples
4. Common interview questions and answers for this topic
5. Step-by-step learning guidance
6. Career development tips related to this topic
${sectionIndex === totalSections - 1 ? '7. Closing remarks and next episode preview' : '7. Transition to next section'}

IMPORTANT: Return ONLY the section content as plain Korean text. Do NOT include any JSON formatting, markdown, or code blocks. Start directly with the Korean podcast content:`;

    const response = await aiSummaryService.generateSummary(sectionPrompt, 'text');
    return response.trim();
  };

  // AI 스크립트 섹션별 생성 함수
  const generateAIScript = async () => {
    if (!aiSummaryService.isAvailable) {
      alert('AI 서비스가 사용 불가능합니다. .env 파일을 확인하세요.');
      return;
    }

    setIsGeneratingScript(true);
    setIsGeneratingStepByStep(true);
    setScriptData(null);
    setGenerationProgress({});
    
    try {
      const currentModule = modules.find(m => m.id === selectedModule);
      const moduleName = currentModule?.name || selectedModule;
      


      // 1. AI요약 데이터 조회
      let summaryContent = 'NCS 수행준거 기반으로 작성합니다.';
      
      setGenerationProgress({ step: 'summary', status: 'loading', message: 'AI요약 데이터 조회 중 (캐시에서)...' });

      try {
        // 캐시된 데이터 사용 (매우 빠름)
        const summaryData = getCachedSummaryData(selectedModule);
        
        if (summaryData && summaryData.length > 0) {
          summaryContent = summaryData.map((item, index) => `
📌 주제: ${item.토픽명 || `주제 ${index + 1}`}
💡 핵심개념: ${item.핵심개념 || ''}
📝 상세설명: ${item.상세설명?.substring(0, 300) || ''}
🔧 실무활용: ${item.실무활용 || ''}
🏷️ 키워드: ${item.키워드 || ''}
          `).join('\n---\n');
          console.log(`✨ ${selectedModule} 데이터 ${summaryData.length}개 사용 (캐시에서)`);
        } else {
          console.log('📊 캐시된 데이터가 없음, 기본 NCS 데이터 사용');
        }

        setGenerationProgress({ step: 'summary', status: 'completed', message: `AI요약 데이터 로드 완료 (캐시: ${summaryData?.length || 0}개)` });
      } catch (error) {
        console.error('❌ AI요약 데이터 로딩 실패:', error);
        setGenerationProgress({ step: 'summary', status: 'completed', message: '기본 NCS 데이터 사용' });
      }

      // 2. 섹션 구조 생성
      setGenerationProgress({ step: 'outline', status: 'loading', message: '섹션 구조 생성 중...' });
      
      const outline = await generateSectionsOutline(moduleName, summaryContent);

      
      setGenerationProgress({ step: 'outline', status: 'completed', message: `섹션 구조 완료 (${outline.sections.length}개)` });

      // 3. 각 섹션별 상세 내용 생성
      const detailedSections = [];
      
      for (let i = 0; i < outline.sections.length; i++) {
        const section = outline.sections[i];
        
        setGenerationProgress({ 
          step: 'sections', 
          status: 'loading', 
          message: `섹션 ${i + 1}/${outline.sections.length} 생성 중...`,
          current: i + 1,
          total: outline.sections.length,
          sectionTitle: section.section_title
        });
        
        try {
          const detailedContent = await generateSectionDetail(
            section, 
            moduleName, 
            summaryContent, 
            i, 
            outline.sections.length
          );
          
          // 이제 AI API에서 순수 텍스트로 응답하므로 바로 사용

          
          detailedSections.push({
            section_order: section.section_order,
            section_title: section.section_title,
            section_content: detailedContent
          });
          

          
          // 각 섹션 완료 후 임시 저장
          const tempScript = {
            course_id: selectedModule,
            course_title: outline.course_title,
            sections: detailedSections,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            description: `${moduleName} 팟캐스트 - 섹션별 심화 학습 (${detailedSections.length}/${outline.sections.length} 완료)`,
            total_duration_minutes: Math.round(detailedSections.length * 7),
            estimated_characters: detailedSections.reduce((sum, s) => sum + s.section_content.length, 0)
          };
          
          setScriptData(tempScript);
          
        } catch (error) {
          console.error(`❌ 섹션 ${i + 1} 생성 실패:`, error);
          
          // 실패한 섹션은 기본 내용으로 대체
          detailedSections.push({
            section_order: section.section_order,
            section_title: section.section_title,
            section_content: `안녕하세요 여러분! 이 섹션은 "${section.section_title}"에 대해 다룹니다. ${section.description} 관련 내용을 더 자세히 설명드리겠습니다. (생성 중 오류가 발생하여 간략히 작성되었습니다)`
          });
        }
        
        // API 레이트 제한 방지
        if (i < outline.sections.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // 4. 최종 스크립트 완성
      const finalScript = {
        course_id: selectedModule,
        course_title: outline.course_title,
        sections: detailedSections,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        description: `${moduleName} 팟캐스트 - 비전공자도 이해하는 ${Math.round(detailedSections.length * 7)}분 심화 학습 (섹션별 생성)`,
        total_duration_minutes: Math.round(detailedSections.length * 7),
        estimated_characters: detailedSections.reduce((sum, s) => sum + s.section_content.length, 0)
      };
      
      setScriptData(finalScript);
      setGenerationProgress({ step: 'completed', status: 'completed', message: '모든 섹션 생성 완료!' });
      

      alert(`🎉 섹션별 AI 스크립트 생성 완료!\n- 제목: ${finalScript.course_title}\n- 섹션: ${finalScript.sections.length}개\n- 총 글자수: ${finalScript.estimated_characters.toLocaleString()}자\n- 예상 시간: ${finalScript.total_duration_minutes}분`);
      
    } catch (error) {
      console.error('❌ 섹션별 AI 스크립트 생성 실패:', error);
      setGenerationProgress({ step: 'error', status: 'error', message: `생성 실패: ${error.message}` });
      alert(`❌ AI 스크립트 생성 실패\n\n${error.message}`);
    } finally {
      setIsGeneratingScript(false);
      setIsGeneratingStepByStep(false);
    }
  };

  // TTSModuleService를 사용한 모듈 데이터 로드 (캐시 사용)
  const loadCurrentModuleData = async () => {
    setIsLoadingExistingData(true);
    try {
      console.log(`📊 ${selectedModule} 모듈 데이터 로드 (캐시 활용)`);

      // 캐시된 TTS 데이터 먼저 확인
      const cachedTTS = await getCachedTTSData(selectedModule);
      const result = cachedTTS ?
        { success: true, data: cachedTTS } :
        await ttsModuleService.loadModuleData(selectedModule);
      
      if (result.success && result.data) {


        
        // 섹션별 데이터 검증
        if (result.data.sections && result.data.sections.length > 0) {
          result.data.sections.forEach((section, index) => {
            console.log(`섹션 ${index + 1}:`, {
              title: section.section_title,
              contentLength: section.section_content?.length || 0,
              content: section.section_content?.substring(0, 100) + '...'
            });
          });
        }
        
        setLoadedModuleData(result.data);

      } else {

        setLoadedModuleData(null);
        alert('기존 TTS 데이터를 찾을 수 없습니다.');
      }
    } catch (error) {
      console.error('❌ 모듈 데이터 로드 실패:', error);
      setLoadedModuleData(null);
      alert('데이터 로드 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setIsLoadingExistingData(false);
    }
  };

  // 개별 섹션 TTS 생성
  const generateSectionTTS = async (sectionIndex) => {
    const currentScript = isEditingScript ? editedScript : scriptData;
    if (!currentScript || !currentScript.sections || !currentScript.sections[sectionIndex]) {
      alert('유효한 섹션이 없습니다.');
      return;
    }

    const section = currentScript.sections[sectionIndex];
    const result = await ttsModuleService.generateSectionTTS(sectionIndex, section, {
      languageCode: 'ko-KR',
      voiceName: 'ko-KR-Chirp3-HD-Algieba',
      gender: 'FEMALE',
      speakingRate: 1.0,
      pitch: 0.0
    });

    if (result.success) {
      alert(`✅ 섹션 "${section.section_title}" TTS 생성 및 다운로드 완료!`);
    } else {
      alert(`❌ 섹션 TTS 생성 실패:\n${result.error}`);
    }
  };

  // 단일 TTS 생성 (ko-KR-Chirp3-HD-Algieba 고정)
  const generateTTS = async () => {
    const currentScript = isEditingScript ? editedScript : scriptData;
    
    if (!currentScript || !currentScript.sections || currentScript.sections.length === 0) {
      alert('먼저 스크립트를 생성해주세요.');
      return;
    }

    setIsGeneratingTTS(true);
    setTtsProgress({});
    
    const result = await ttsModuleService.generateAllSectionsTTS(currentScript.sections, {
      languageCode: 'ko-KR',
      voiceName: 'ko-KR-Chirp3-HD-Algieba',
      gender: 'FEMALE',
      speakingRate: 1.0,
      pitch: 0.0
    });

    setIsGeneratingTTS(false);

    if (result.success) {
      alert(`🎵 전체 TTS 생성 완료!\n성공: ${result.successCount}/${result.totalCount}개 섹션`);
    } else {
      alert(`⚠️ TTS 생성 부분 완료\n성공: ${result.successCount}/${result.totalCount}개 섹션`);
    }
  };

  // TTSTest 스타일의 TTS 생성 (기존 방식 유지)
  const generateFullTTS = async () => {
    const currentScript = isEditingScript ? editedScript : scriptData;
    
    if (!currentScript || !currentScript.sections || currentScript.sections.length === 0) {
      alert('먼저 스크립트를 생성해주세요.');
      return;
    }

    setIsGeneratingTTS(true);
    setAudioUrls([]);
    
    try {

      
      const allAudioUrls = [];
      
      // 각 섹션별로 개별 TTS 생성
      for (let i = 0; i < currentScript.sections.length; i++) {
        const section = currentScript.sections[i];
        const sectionText = section.section_content || '';
        
        if (!sectionText.trim()) {

          continue;
        }
        



        
        try {
          // 개별 섹션 TTS 생성
          const sectionAudioUrls = await googleTTSService.createPodcastAudioWithConfig(
            sectionText,
            {
                "audioConfig": {
                  "audioEncoding": "LINEAR16",
                  "effectsProfileId": ["small-bluetooth-speaker-class-device"],
                  "pitch": 0,
                  "speakingRate": 1
                },
                "voice": {
                  "languageCode": "ko-KR",
                  "name": "ko-KR-Chirp3-HD-Algenib"
                }
              },
            (current, total, message) => {

            }
          );
          
          // 섹션별 오디오 URL들을 전체 배열에 추가
          allAudioUrls.push(...sectionAudioUrls);
          

          
          // API 레이트 제한 방지를 위한 딜레이
          if (i < currentScript.sections.length - 1) {

            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
        } catch (error) {
          console.error(`❌ 섹션 ${i + 1} TTS 생성 실패:`, error);
          alert(`섹션 "${section.section_title}" TTS 생성 실패:\n${error.message}\n\n계속 진행하시겠습니까?`);
        }
      }
      
      setAudioUrls(allAudioUrls);

      
      const totalCharacters = currentScript.sections.reduce((sum, section) => sum + (section.section_content?.length || 0), 0);
      alert(`🎵 전체 TTS 생성 완료!\n- 총 섹션: ${currentScript.sections.length}개\n- 총 세그먼트: ${allAudioUrls.length}개\n- 예상 시간: ${Math.round(totalCharacters / 300)}분`);
      
    } catch (error) {
      console.error('TTS 생성 실패:', error);
      alert(`❌ TTS 생성 실패\n\n${error.message}`);
    } finally {
      setIsGeneratingTTS(false);
    }
  };

  // TTS 오디오 다운로드 함수
  const downloadTTSAudio = () => {
    if (!audioUrls || audioUrls.length === 0) {
      alert('먼저 TTS를 생성해주세요.');
      return;
    }

    const currentScript = isEditingScript ? editedScript : scriptData;
    const fileName = currentScript?.course_title || `${selectedModule}_podcast`;
    googleTTSService.downloadMultipleAudios(audioUrls, fileName);
    
    alert(`💾 다운로드 시작!\n파일명: ${fileName}\n세그먼트: ${audioUrls.length}개`);
  };

  // Google Sheets에 스크립트 저장 함수
  const saveScriptToSheets = async () => {
    const currentScript = isEditingScript ? editedScript : scriptData;
    
    if (!currentScript || !currentScript.sections || currentScript.sections.length === 0) {
      alert('저장할 스크립트가 없습니다.');
      return;
    }

    setIsSaving(true);
    
    try {

      
      // TTS_Scripts 형식에 맞춰 데이터 준비
      const scriptEntries = [];
      
      // 각 섹션을 개별 행으로 저장
      for (let i = 0; i < currentScript.sections.length; i++) {
        const section = currentScript.sections[i];
        
        const entry = {
          course_id: currentScript.course_id || selectedModule,
          course_title: currentScript.course_title || `${selectedModule} 가이드`,
          section_order: section.section_order || (i + 1),
          section_title: section.section_title || `섹션 ${i + 1}`,
          section_content: section.section_content || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        scriptEntries.push(entry);
      }
      


      
      // Google Sheets에 배치 저장
      for (let i = 0; i < scriptEntries.length; i++) {
        const entry = scriptEntries[i];
        
        console.log(`저장 중 (${i + 1}/${scriptEntries.length}):`, {
          course_id: entry.course_id,
          course_title: entry.course_title,
          section_order: entry.section_order,
          section_title: entry.section_title,
          content_length: entry.section_content.length
        });
        
        try {

          
          const result = await appScriptService.addData('TTS_Scripts', entry);
          


        } catch (error) {
          console.error('❌ 저장 실패:', entry.section_title, error);
          console.error('실패한 데이터:', JSON.stringify(entry, null, 2));
          throw new Error(`섹션 "${entry.section_title}" 저장 실패: ${error.message}`);
        }
        
        // API 레이트 제한 방지를 위한 딜레이
        if (i < scriptEntries.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      

      alert(`✅ Google Sheets 저장 완료!\n- 코스: ${currentScript.course_title}\n- 섹션: ${currentScript.sections.length}개\n\n이제 생성된 TTS 파일을 GCS에 업로드하면 Podcast 페이지에서 사용할 수 있습니다.`);
      
    } catch (error) {
      console.error('Google Sheets 저장 실패:', error);
      alert(`❌ 저장 실패\n\n${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // 편집 관련 함수들
  const startEditingScript = () => {
    if (!scriptData || !scriptData.sections || scriptData.sections.length === 0) {
      alert('편집할 스크립트가 없습니다. 먼저 스크립트를 생성해주세요.');
      return;
    }
    
    // 현재 스크립트의 깊은 복사본 생성
    setEditedScript({
      ...scriptData,
      sections: scriptData.sections.map(section => ({ ...section }))
    });
    setIsEditingScript(true);
  };

  const cancelEditingScript = () => {
    setEditedScript(null);
    setIsEditingScript(false);
  };

  const saveEditedScript = () => {
    if (!editedScript) return;
    
    setScriptData(editedScript);
    setIsEditingScript(false);
    setEditedScript(null);
    alert('✅ 스크립트 편집이 저장되었습니다!');
  };

  const updateSectionContent = (sectionIndex, newContent) => {
    if (!editedScript) return;
    
    const updatedScript = {
      ...editedScript,
      sections: editedScript.sections.map((section, index) => 
        index === sectionIndex ? { ...section, section_content: newContent } : section
      )
    };
    setEditedScript(updatedScript);
  };

  const updateSectionTitle = (sectionIndex, newTitle) => {
    if (!editedScript) return;
    
    const updatedScript = {
      ...editedScript,
      sections: editedScript.sections.map((section, index) => 
        index === sectionIndex ? { ...section, section_title: newTitle } : section
      )
    };
    setEditedScript(updatedScript);
  };

  // 섹션 삭제 함수
  const deleteSection = (sectionIndex) => {
    if (!editedScript || !editedScript.sections) return;
    
    if (editedScript.sections.length <= 1) {
      alert('최소 1개의 섹션은 유지해야 합니다.');
      return;
    }
    
    const sectionTitle = editedScript.sections[sectionIndex]?.section_title || `섹션 ${sectionIndex + 1}`;
    
    if (window.confirm(`"${sectionTitle}" 섹션을 삭제하시겠습니까?\n\n삭제 후에는 복구할 수 없습니다.`)) {
      const updatedScript = {
        ...editedScript,
        sections: editedScript.sections.filter((_, index) => index !== sectionIndex)
          .map((section, index) => ({
            ...section,
            section_order: index + 1 // 섹션 순서 재정렬
          }))
      };
      setEditedScript(updatedScript);

    }
  };

  // 새 섹션 추가 함수
  const addNewSection = () => {
    if (!editedScript || !editedScript.sections) return;
    
    if (editedScript.sections.length >= 10) {
      alert('섹션은 최대 10개까지만 추가할 수 있습니다.');
      return;
    }
    
    const newSectionOrder = editedScript.sections.length + 1;
    const newSection = {
      section_order: newSectionOrder,
      section_title: `새로운 섹션 ${newSectionOrder}`,
      section_content: '새로운 섹션의 내용을 작성해주세요. 팟캐스트에 적합한 친근하고 대화하는 듯한 어조로 작성해보세요.'
    };
    
    const updatedScript = {
      ...editedScript,
      sections: [...editedScript.sections, newSection]
    };
    
    setEditedScript(updatedScript);

  };

  // 모듈 변경 핸들러
  const handleModuleChange = (e) => {
    setSelectedModule(e.target.value);
    setScriptData(null);
    setAudioUrls([]);
    setIsEditingScript(false);
    setEditedScript(null);
    setLoadedModuleData(null);
    setTtsProgress({});
    setIsLoadingExistingData(false);
  };

  // TTS_Scripts에서 기존 데이터 로드
  const loadExistingTTSData = async () => {
    await loadCurrentModuleData();
  };

  // 기존 데이터를 scriptData로 변환하여 사용
  const useExistingData = () => {
    if (!loadedModuleData || !loadedModuleData.sections) {
      alert('먼저 기존 데이터를 로드해주세요.');
      return;
    }

    // loadedModuleData를 scriptData 형식으로 변환
    const convertedData = {
      course_id: selectedModule,
      course_title: loadedModuleData.title,
      sections: loadedModuleData.sections.map((section, index) => ({
        section_order: section.section_order || (index + 1),
        section_title: section.section_title,
        section_content: extractTextFromContent(section.section_content)
      })),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      description: `${loadedModuleData.title} - 기존 TTS 스크립트 활용`,
      total_duration_minutes: Math.round(loadedModuleData.sections.reduce((total, section) => 
        total + (section.section_content?.length || 0), 0) / 300),
      estimated_characters: loadedModuleData.sections.reduce((total, section) => 
        total + (section.section_content?.length || 0), 0)
    };

    setScriptData(convertedData);

    alert(`✨ 기존 데이터 로드 완료!\n- 제목: ${convertedData.course_title}\n- 섹션: ${convertedData.sections.length}개\n\n이제 편집, TTS 생성, 저장이 가능합니다.`);
  };

  // 섹션 내용에서 JSON 형태를 제거하고 텍스트만 추출하는 함수
  const extractTextFromContent = (content) => {
    if (!content) return '';
    
    try {
      const trimmedContent = content.trim();
      // JSON 형태인지 확인
      if (trimmedContent.startsWith('{') && trimmedContent.endsWith('}')) {
        const parsedContent = JSON.parse(trimmedContent);
        if (parsedContent.section_content && typeof parsedContent.section_content === 'string') {
          return parsedContent.section_content;
        }
      }
      return content; // JSON이 아니거나 파싱에 실패한 경우 원본 반환
    } catch (error) {
      return content; // 파싱 실패 시 원본 반환
    }
  };

  // 기존 데이터를 편집 모드로 바로 전환
  const editExistingData = () => {
    if (!loadedModuleData || !loadedModuleData.sections) {
      alert('먼저 기존 데이터를 로드해주세요.');
      return;
    }

    // loadedModuleData를 scriptData 형식으로 변환
    const convertedData = {
      course_id: selectedModule,
      course_title: loadedModuleData.title,
      sections: loadedModuleData.sections.map((section, index) => ({
        section_order: section.section_order || (index + 1),
        section_title: section.section_title,
        section_content: extractTextFromContent(section.section_content)
      })),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      description: `${loadedModuleData.title} - 기존 TTS 스크립트 활용`,
      total_duration_minutes: Math.round(loadedModuleData.sections.reduce((total, section) => 
        total + (section.section_content?.length || 0), 0) / 300),
      estimated_characters: loadedModuleData.sections.reduce((total, section) => 
        total + (section.section_content?.length || 0), 0)
    };

    // 편집 모드로 직접 전환
    setEditedScript({
      ...convertedData,
      sections: convertedData.sections.map(section => ({ ...section }))
    });
    setScriptData(convertedData);
    setIsEditingScript(true);
    

    alert(`📝 편집 모드로 전환되었습니다!\n- 제목: ${convertedData.course_title}\n- 섹션: ${convertedData.sections.length}개\n\n편집을 완료하고 저장해주세요.`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* 헤더 */}
        <div className="text-center py-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-4">
            🎵 교사용 TTS 스크립트 관리
          </h1>
          <p className="text-gray-600 text-lg">
            NCS 모듈별 AI 팟캐스트 스크립트 생성, 편집, TTS 변환 및 관리
          </p>
        </div>

        {/* 모듈 선택 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4">📚 NCS 모듈 선택</h2>
          
          <div className="space-y-4">
            <select
              value={selectedModule}
              onChange={handleModuleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
            >
              {modules.map(module => (
                <option key={module.id} value={module.id}>
                  {module.name} {
                    module.isChecking ? '🔍 확인중...' :
                    module.hasData ? `✅ 데이터 ${module.dataCount}개` : 
                    '⚠️ 데이터 없음'
                  }
                </option>
              ))}
            </select>
            
            <div className="text-sm text-gray-600">
              💡 모듈을 선택하면 해당 모듈의 AI요약 데이터를 기반으로 팟캐스트 스크립트를 생성합니다.
            </div>
            
            {/* 선택된 모듈의 상세 정보 */}
            {(() => {
              const currentModule = modules.find(m => m.id === selectedModule);
              return currentModule && !currentModule.isChecking && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">📊 선택된 모듈 정보</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <div>• TTS 스크립트: {currentModule.hasTTSData ? '✅ 있음' : '❌ 없음'}</div>
                    <div>• AI 요약 데이터: {currentModule.hasSummaryData ? '✅ 있음' : '❌ 없음'}</div>
                    <div>• 총 데이터 개수: {currentModule.dataCount}개</div>
                    {!currentModule.hasData && (
                      <div className="text-orange-600 font-medium">
                        ⚠️ 이 모듈은 스크립트 생성만 가능합니다 (기본 NCS 기반)
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
        

        {/* AI 스크립트 생성 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4">🤖 AI 스크립트 생성</h2>
          
          <div className="space-y-4">
            <button
              onClick={generateAIScript}
              disabled={isGeneratingScript}
              className={`w-full px-6 py-4 rounded-lg font-semibold text-white text-lg transition-all ${
                isGeneratingScript
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transform hover:scale-105'
              }`}
            >
              {isGeneratingScript ? '🔄 AI 스크립트 생성 중...' : '✨ AI 팟캐스트 스크립트 생성'}
            </button>
            
            {/* 섹션별 생성 진행률 표시 */}
            {isGeneratingStepByStep && Object.keys(generationProgress).length > 0 && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">🔄 섹션별 AI 생성 진행률</h3>
                
                <div className="space-y-3">
                  {/* 1단계: 요약 데이터 조회 */}
                  <div className={`flex items-center space-x-3 p-2 rounded ${
                    generationProgress.step === 'summary' ? 'bg-white border' : ''
                  }`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${
                      generationProgress.step === 'summary' && generationProgress.status === 'loading' ? 'bg-blue-600 text-white animate-pulse' :
                      generationProgress.step === 'summary' && generationProgress.status === 'completed' ? 'bg-green-600 text-white' :
                      ['outline', 'sections', 'completed'].includes(generationProgress.step) ? 'bg-green-600 text-white' :
                      'bg-gray-300'
                    }`}>
                      {['outline', 'sections', 'completed'].includes(generationProgress.step) || 
                       (generationProgress.step === 'summary' && generationProgress.status === 'completed') ? '✓' : '1'}
                    </div>
                    <span className="text-sm">요약 데이터 조회</span>
                    {generationProgress.step === 'summary' && (
                      <span className="text-xs text-blue-600 ml-auto">{generationProgress.message}</span>
                    )}
                  </div>
                  
                  {/* 2단계: 섹션 구조 생성 */}
                  <div className={`flex items-center space-x-3 p-2 rounded ${
                    generationProgress.step === 'outline' ? 'bg-white border' : ''
                  }`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${
                      generationProgress.step === 'outline' && generationProgress.status === 'loading' ? 'bg-blue-600 text-white animate-pulse' :
                      generationProgress.step === 'outline' && generationProgress.status === 'completed' ? 'bg-green-600 text-white' :
                      ['sections', 'completed'].includes(generationProgress.step) ? 'bg-green-600 text-white' :
                      'bg-gray-300'
                    }`}>
                      {['sections', 'completed'].includes(generationProgress.step) || 
                       (generationProgress.step === 'outline' && generationProgress.status === 'completed') ? '✓' : '2'}
                    </div>
                    <span className="text-sm">섹션 구조 생성</span>
                    {generationProgress.step === 'outline' && (
                      <span className="text-xs text-blue-600 ml-auto">{generationProgress.message}</span>
                    )}
                  </div>
                  
                  {/* 3단계: 섹션별 상세 생성 */}
                  <div className={`flex items-center space-x-3 p-2 rounded ${
                    generationProgress.step === 'sections' ? 'bg-white border' : ''
                  }`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs ${
                      generationProgress.step === 'sections' && generationProgress.status === 'loading' ? 'bg-blue-600 text-white animate-pulse' :
                      generationProgress.step === 'sections' && generationProgress.status === 'completed' ? 'bg-green-600 text-white' :
                      generationProgress.step === 'completed' ? 'bg-green-600 text-white' :
                      'bg-gray-300'
                    }`}>
                      {generationProgress.step === 'completed' || 
                       (generationProgress.step === 'sections' && generationProgress.status === 'completed') ? '✓' : '3'}
                    </div>
                    <div className="flex-1">
                      <span className="text-sm">섹션별 상세 내용 생성</span>
                      {generationProgress.step === 'sections' && generationProgress.current && (
                        <div className="mt-1">
                          <div className="text-xs text-blue-600 mb-1">
                            {generationProgress.message} - {generationProgress.sectionTitle}
                          </div>
                          <div className="w-full bg-blue-200 rounded-full h-1.5">
                            <div 
                              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${(generationProgress.current / generationProgress.total) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* 완료 상태 */}
                  {generationProgress.step === 'completed' && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs">✓</div>
                        <span className="text-green-800 font-medium">{generationProgress.message}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* 오류 상태 */}
                  {generationProgress.step === 'error' && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center text-xs">!</div>
                        <span className="text-red-800 font-medium">{generationProgress.message}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {scriptData && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  {isGeneratingStepByStep ? '🔄 스크립트 생성 중...' : '✅ 스크립트 생성 완료!'}
                </h3>
                <div className="text-green-700 space-y-1">
                  <p><strong>제목:</strong> {scriptData.course_title}</p>
                  <p><strong>섹션 수:</strong> {scriptData.sections?.length || 0}개</p>
                  <p><strong>예상 시간:</strong> {scriptData.total_duration_minutes || 0}분</p>
                  <p><strong>예상 글자수:</strong> {scriptData.estimated_characters?.toLocaleString() || 0}자</p>
                  {scriptData.description?.includes('섹션별') && (
                    <p><strong>생성 방식:</strong> 섹션별 개별 생성 (고품질)</p>
                  )}
                </div>
                
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={startEditingScript}
                    disabled={isGeneratingStepByStep}
                    className={`px-4 py-2 font-medium rounded-lg transition-all ${
                      isGeneratingStepByStep 
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    ✏️ 스크립트 편집
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 기존 TTS 데이터 로드 - 조건부 표시 */}
        {(() => {
          const currentModule = modules.find(m => m.id === selectedModule);
          return currentModule && currentModule.hasTTSData && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-semibold mb-4">📋 기존 TTS 스크립트 확인</h2>
              
              <div className="space-y-4">
                <button
                  onClick={loadExistingTTSData}
                  disabled={isLoadingExistingData}
                  className={`w-full px-6 py-3 font-semibold rounded-lg transition-all ${
                    isLoadingExistingData
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isLoadingExistingData ? '🔄 데이터 로딩 중...' : '🔍 기존 데이터 로드'}
                </button>
                
                {loadedModuleData && (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">✅ 기존 TTS 데이터 발견!</h3>
                      <div className="text-green-700 space-y-1">
                        <p><strong>제목:</strong> {loadedModuleData.title}</p>
                        <p><strong>섹션 수:</strong> {loadedModuleData.sections?.length || 0}개</p>
                        <p><strong>총 분량:</strong> 약 {Math.round(loadedModuleData.sections?.reduce((total, section) => 
                          total + (section.section_content?.length || 0), 0) / 300) || 0}분</p>
                        <p><strong>글자 수:</strong> {loadedModuleData.sections?.reduce((total, section) => 
                          total + (section.section_content?.length || 0), 0).toLocaleString() || 0}자</p>
                      </div>
                    </div>
                    
                    {/* 기존 데이터 미리보기 */}
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <h4 className="font-medium text-gray-800 mb-3">📋 섹션 미리보기</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {loadedModuleData.sections?.map((section, index) => (
                          <div key={index} className="text-sm">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs mr-2">
                              {section.section_order || index + 1}
                            </span>
                            <span className="font-medium">{section.section_title}</span>
                            <span className="text-gray-500 ml-2">
                              ({section.section_content?.length || 0}자)
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* 액션 버튼 */}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={useExistingData}
                        className="flex-1 min-w-[140px] px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all"
                      >
                        📖 기존 데이터 사용
                      </button>
                      
                      <button
                        onClick={editExistingData}
                        className="flex-1 min-w-[140px] px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all"
                      >
                        ✏️ 편집하기
                      </button>
                      
                      <button
                        onClick={() => {
                          setLoadedModuleData(null);

                        }}
                        className="flex-1 min-w-[140px] px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-all"
                      >
                        🤖 새로 AI 생성
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}


        {/* 스크립트 편집 모달 */}
        {isEditingScript && editedScript && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* 헤더 */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold text-gray-800">
                    ✏️ 팟캐스트 스크립트 편집: {editedScript.course_title}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={saveEditedScript}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                    >
                      💾 저장
                    </button>
                    <button
                      onClick={cancelEditingScript}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm font-medium"
                    >
                      ❌ 취소
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  🎙️ 팟캐스트 스크립트를 편집하세요. TTS 음성으로 변환되므로 자연스러운 구어체로 작성해주세요.
                </p>
              </div>

              {/* 편집 영역 */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-800">
                    📝 섹션 편집 ({editedScript.sections?.length || 0}개)
                  </h3>
                  <button
                    onClick={addNewSection}
                    className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-all"
                  >
                    <span>➕</span>
                    <span>섹션 추가</span>
                  </button>
                </div>
                
                {editedScript.sections?.map((section, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <span className="bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full">
                          섹션 {section.section_order || index + 1}
                        </span>
                        <button
                          onClick={() => deleteSection(index)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-all text-xs"
                          title="섹션 삭제"
                        >
                          🗑️ 삭제
                        </button>
                      </div>
                      <div className="text-xs text-gray-500">
                        {Math.round((section.section_content?.length || 0) / 300)}분 예상
                      </div>
                    </div>
                    
                    {/* 제목 편집 */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        섹션 제목
                      </label>
                      <input
                        type="text"
                        value={section.section_title || `섹션 ${index + 1}`}
                        onChange={(e) => updateSectionTitle(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="섹션 제목을 입력하세요"
                      />
                    </div>
                    
                    {/* 내용 편집 */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          섹션 내용
                        </label>
                        <div className="text-xs text-gray-500">
                          {section.section_content?.length || 0}자
                        </div>
                      </div>
                      <textarea
                        value={extractTextFromContent(section.section_content) || ''}
                        onChange={(e) => updateSectionContent(index, e.target.value)}
                        rows={12}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-y"
                        placeholder="팟캐스트 스크립트를 입력하세요. 친근하고 대화하는 듯한 어조로 작성해주세요."
                      />
                      <div className="mt-2 text-xs text-gray-500 bg-blue-50 p-2 rounded">
                        💡 팟캐스트 TTS 최적화 팁:
                        • 친근하고 대화하는 듯한 어조 사용
                        • 쉼표(,)와 마침표(.)로 자연스러운 호흡 표현
                        • 복잡한 코드보다는 개념과 비유 중심으로 설명
                        • "여러분", "우리가", "이해되시나요?" 같은 소통하는 표현 활용
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 푸터 */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    <div>총 {editedScript.sections?.length || 0}개 섹션, 약 {Math.round((editedScript.sections?.reduce((total, section) => total + (section.section_content?.length || 0), 0) || 0) / 300)}분 분량</div>
                    <div className="text-xs text-gray-500 mt-1">
                      💡 섹션 추가/삭제 가능 • 최소 1개, 최대 10개 섹션 • {editedScript.sections?.reduce((total, section) => total + (section.section_content?.length || 0), 0).toLocaleString() || 0}자
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveEditedScript}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                    >
                      💾 편집 완료
                    </button>
                    <button
                      onClick={cancelEditingScript}
                      className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium"
                    >
                      ❌ 취소
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* 스크립트 미리보기 및 통합 액션 */}
        {scriptData && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-4">📜 스크립트 미리보기</h2>
            
            <div className="space-y-6">
              {(() => {
                const currentScript = isEditingScript ? editedScript : scriptData;
                return (
                  <>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-800">{currentScript?.course_title || '제목 없음'}</h3>
                          <p className="text-gray-600 mt-2">{currentScript?.description || '설명 없음'}</p>
                        </div>
                        <div className="ml-4">
                          {currentScript?.description?.includes('기존 TTS 스크립트') ? (
                            <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                              📖 기존 데이터
                            </span>
                          ) : (
                            <span className="bg-purple-100 text-purple-800 text-xs font-medium px-3 py-1 rounded-full">
                              🤖 AI 생성
                            </span>
                          )}
                        </div>
                      </div>
                      {isEditingScript && (
                        <div className="mt-2 text-sm text-blue-600">
                          ✏️ 편집 중인 버전을 표시하고 있습니다
                        </div>
                      )}
                    </div>
                    
                    {/* TTS 진행률 표시 (생성 중일 때만) */}
                    {Object.keys(ttsProgress).length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-lg font-medium text-gray-800">📊 TTS 생성 진행률</h4>
                        {Object.entries(ttsProgress).map(([key, progress]) => {
                          if (!progress) return null;
                          return (
                            <div key={key} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-blue-800">
                                  {progress.section || key}
                                </span>
                                <span className="text-xs text-blue-600">
                                  {progress.status === 'generating' && `${progress.chunk}/${progress.totalChunks} 청크`}
                                  {progress.status === 'downloading' && '다운로드 중'}
                                  {progress.status === 'completed' && '완료'}
                                  {progress.status === 'error' && '오류'}
                                </span>
                              </div>
                              {progress.status === 'generating' && (
                                <div className="w-full bg-blue-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${progress.progress || 0}%` }}
                                  ></div>
                                </div>
                              )}
                              {progress.status === 'error' && (
                                <div className="text-red-600 text-xs mt-1">
                                  ❌ {progress.error}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* 통합 액션 바 */}
                    <div className="flex flex-wrap gap-3 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border">
                      <button
                        onClick={startEditingScript}
                        className="flex-1 min-w-[150px] px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all"
                      >
                        ✏️ 스크립트 편집
                      </button>
                      <button
                        onClick={saveScriptToSheets}
                        disabled={isSaving}
                        className={`flex-1 min-w-[150px] px-4 py-3 font-medium rounded-lg transition-all ${
                          isSaving
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {isSaving ? '💾 저장 중...' : '📤 구글시트 저장'}
                      </button>
                      
                      <button
                        onClick={generateTTS}
                        disabled={isGeneratingTTS}
                        className={`flex-1 min-w-[150px] px-4 py-3 font-medium rounded-lg transition-all ${
                          isGeneratingTTS
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {isGeneratingTTS ? '🔄 TTS 생성 중...' : '🎵 TTS 생성 (ko-KR-Chirp3-HD-Algieba)'}
                      </button>
                      

                    </div>
                    
                    {/* 섹션별 내용 */}
                    <div className="space-y-4">
                      {currentScript?.sections && currentScript.sections.map((section, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-lg font-semibold text-gray-800">
                              <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2">
                                {section.section_order || index + 1}
                              </span>
                              {section.section_title || `섹션 ${index + 1}`}
                            </h4>
                            <span className="text-xs text-gray-400">
                              {Math.round((section.section_content?.length || 0) / 300)}분
                            </span>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-sm">
                            {extractTextFromContent(section.section_content) || '내용 없음'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TTSManager;